import * as React from 'react';
import React__default from 'react';
import { Upload as Upload$1 } from '@grandvisionhq/api-client';
import { useRecaptcha } from '@grandvisionhq/forms';
import { useApiConfig, uninitState, statusSuccess, loadingState, successState, failedState, useIntl, statusFailed, statusWarning, statusLoading, statusUninit } from '@grandvisionhq/state';
import classnames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import { Loader, IconButton } from '@grandvisionhq/common';
import { Text } from '@grandvisionhq/elements';
import { Icon } from '@grandvisionhq/icons';

function _defineProperty$3(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread$3(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$3(target, key, source[key]);
        });
    }
    return target;
}
function isFileRejection(error) {
    return 'file' in error;
}
const isUploadSuccess = (state)=>state.status === 'SUCCESS'
;
const isUploadError = (state)=>state.status === 'ERROR'
;
const isUploadIdle = (state)=>state.status === 'IDLE'
;
const isUploadIdleAfterFileRemove = (state)=>state.status === 'IDLE' && state.reason === 'FILE_REMOVED'
;
const isUploadLoading = (state)=>state.status === 'LOADING'
;
const uploadStatusSuccess = (uploadReference)=>({
        status: 'SUCCESS',
        uploadReference
    })
;
const uploadStatusError = (error, errorMessageId)=>({
        status: 'ERROR',
        error,
        errorMessageId
    })
;
const uploadStatusIdle = (reason)=>_objectSpread$3({
        status: 'IDLE'
    }, reason ? {
        reason
    } : {})
;
const uploadStatusLoading = ()=>({
        status: 'LOADING'
    })
;

var types = /*#__PURE__*/Object.freeze({
  __proto__: null,
  isFileRejection: isFileRejection,
  isUploadSuccess: isUploadSuccess,
  isUploadError: isUploadError,
  isUploadIdle: isUploadIdle,
  isUploadIdleAfterFileRemove: isUploadIdleAfterFileRemove,
  isUploadLoading: isUploadLoading,
  uploadStatusSuccess: uploadStatusSuccess,
  uploadStatusError: uploadStatusError,
  uploadStatusIdle: uploadStatusIdle,
  uploadStatusLoading: uploadStatusLoading
});

const createNoFileError = ()=>new Error('no-file')
;
const LABEL_PREFIX = 'upload.error';
const labelMapping = {
    'file-invalid-type': `${LABEL_PREFIX}.fileInvalidType`,
    'file-too-large': `${LABEL_PREFIX}.fileTooLarge`,
    'file-too-small': `${LABEL_PREFIX}.fileTooSmall`,
    'too-many-files': `${LABEL_PREFIX}.tooManyFiles`,
    'no-file': `${LABEL_PREFIX}.noFile`,
    unknown: `${LABEL_PREFIX}.unknown`
};
const getUploadErrorLabel = (currentError)=>(isFileRejection(currentError) ? labelMapping[currentError.errors[0].code] : labelMapping[currentError.message]) || labelMapping.unknown
;

function _defineProperty$2(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread$2(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$2(target, key, source[key]);
        });
    }
    return target;
}
function useUploadHandler(props) {
    const [lastError, setLastError] = React__default.useState(()=>{
        /**
     * It should check the uploadStatus for all config.handlers and if one of them is in
     * error state the {lastError}, should reflect it.It should also check
     * if config.checkRequired is being set, and if so checks for required property in each handler.
     */ const { isRequired: isRequired1 , hasError: hasError1  } = props.configs.reduce(({ isRequired , hasError  }, { required , uploadStatus  })=>({
                isRequired: isRequired || required,
                hasError: hasError || !!(uploadStatus && isUploadError(uploadStatus))
            })
        , {
            isRequired: false,
            hasError: false
        });
        if (hasError1 || props.checkRequired && isRequired1) {
            return createNoFileError();
        }
        return undefined;
    });
    const [, setIsSubmitted] = React__default.useState(!!props.checkRequired);
    const handlers = React__default.useRef(props.configs.reduce((h, { scope  })=>_objectSpread$2({}, h, {
            [scope]: getStatusChangeHandler(scope)
        })
    , {}));
    const [state, setState] = React__default.useState(props.configs.reduce((s, { scope , uploadStatus , required  })=>{
        if (uploadStatus && isUploadSuccess(uploadStatus)) {
            return _objectSpread$2({}, s, {
                [scope]: uploadStatus
            });
        }
        if (required && props.checkRequired) {
            return _objectSpread$2({}, s, {
                [scope]: uploadStatusError(createNoFileError(), props.sharedErrorElementId)
            });
        }
        return _objectSpread$2({}, s, {
            [scope]: uploadStatusIdle()
        });
    }, {}));
    function getStatusChangeHandler(scope1) {
        var ref;
        const isRequired = (ref = props.configs.find(({ scope  })=>scope === scope
        )) === null || ref === void 0 ? void 0 : ref.required;
        return (statusUpdate)=>{
            setIsSubmitted((prevIsSubmitted)=>{
                setState((prevState)=>Object.entries(prevState).reduce((m, [key, status])=>{
                        if (key === scope1) {
                            const possiblyError = isUploadError(statusUpdate) ? statusUpdate.error : null;
                            /**
               * If the status update is indicating that a required file will be removed, we'll override
               * the error to be a "no-file" error.
               */ const error = isUploadIdle(statusUpdate) && isRequired && prevIsSubmitted ? new Error('no-file') : possiblyError;
                            setLastError(error || undefined);
                            return _objectSpread$2({}, m, {
                                [key]: error ? uploadStatusError(error, props.sharedErrorElementId) : statusUpdate
                            });
                        }
                        /**
             * To only show the last triggered status update, we're setting all other scopes to IDLE in
             * case they are in ERROR state. We want to keep all scopes that are SUCCESS as is.
             */ return _objectSpread$2({}, m, {
                            [key]: isUploadError(status) ? uploadStatusIdle() : status
                        });
                    }, {})
                );
                return prevIsSubmitted;
            });
        };
    }
    function checkMissingFiles() {
        let hasMissingFiles = false;
        const newState = props.configs.reduce((m, { scope , required  })=>{
            if (required && !isUploadSuccess(state[scope])) {
                const error = createNoFileError();
                setLastError(error);
                hasMissingFiles = true;
                return _objectSpread$2({}, m, {
                    [scope]: uploadStatusError(error)
                });
            }
            return m;
        }, {});
        setState((prevState)=>_objectSpread$2({}, prevState, newState)
        );
        setIsSubmitted(true);
        return hasMissingFiles;
    }
    return {
        lastError: lastError ? getUploadErrorLabel(lastError) : null,
        checkMissingFiles,
        state,
        handlers: handlers.current
    };
}

function _defineProperty$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread$1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$1(target, key, source[key]);
        });
    }
    return target;
}
const UploadContext = /*#__PURE__*/ React.createContext({});
const useUpload = ()=>React.useContext(UploadContext)
;
const UploadProvider = ({ scopes , shouldFetchConfigs =true , timeout =20000 , children ,  })=>{
    const timersRef = React.useRef({});
    const mountRef = React.useRef(true);
    const { apiUrl  } = useApiConfig();
    const [state, dispatch] = React.useReducer((s, action)=>{
        if (action.name === 'error') {
            return _objectSpread$1({}, s, {
                error: action.value
            });
        }
        // updating upload config
        return _objectSpread$1({}, s, {
            configs: _objectSpread$1({}, s.configs, {
                [action.name]: action.value
            })
        });
    }, {
        scopes,
        error: null,
        configs: scopes.reduce((m, scope)=>_objectSpread$1({}, m, {
                [scope]: uninitState()
            })
        , {})
    });
    function setError(error) {
        dispatch({
            name: 'error',
            value: error
        });
    }
    function updateConfig(scope, result) {
        // do not update component state if it has been unmounted
        if (!mountRef.current) {
            return;
        }
        dispatch({
            name: scope,
            value: result
        });
    }
    React.useEffect(()=>{
        // if the upload radio option does not exist => do not fetch configs
        if (!shouldFetchConfigs) {
            return;
        }
        mountRef.current = true;
        // use one signal to rule them all
        const abortController = new AbortController();
        const signal = abortController.signal;
        for (const scope of scopes){
            /**
       * If we already have successfully fetched the config for
       * this scope, there's no need to fetch it again.
       */ const scopeState = state[scope];
            if (scopeState && statusSuccess(scopeState)) {
                continue;
            }
            updateConfig(scope, loadingState());
            // setting the timer per scope
            timersRef.current[scope] = window.setTimeout(()=>{
                abortController.abort();
            }, timeout);
            // calling API for upload config
            Upload$1.getConfig(apiUrl, {
                scope
            }, {
                signal
            }).then((response)=>{
                clearTimeout(timersRef.current[scope]);
                updateConfig(scope, successState(response));
            }).catch((error)=>{
                clearTimeout(timersRef.current[scope]);
                updateConfig(scope, failedState(error));
            });
        }
        return ()=>{
            // Cancel the running requests when this component is unmounted.
            abortController.abort();
            mountRef.current = false;
        };
    }, [
        scopes,
        shouldFetchConfigs
    ]);
    return(/*#__PURE__*/ React.createElement(UploadContext.Provider, {
        value: _objectSpread$1({}, state, {
            setError
        })
    }, children));
};

function _extends$1() {
    _extends$1 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$1.apply(this, arguments);
}
const iconSharedProps = {
    initial: {
        opacity: 0,
        scale: 0
    },
    animate: {
        opacity: 1,
        scale: 1
    },
    exit: {
        opacity: 0,
        scale: 0
    }
};
const Upload = ({ variant ='vertical' , id , iconName , disabled =false , maximumFileSizeBytes , allowedFileTypes , title , uploadStatus , onFileAccepted , onFileRejected , onDelete , onCancel , className , 'aria-describedby': ariaDescribedBy  })=>{
    const { getFormattedLabel , getLabel , hasLabel  } = useIntl();
    const descriptionId = `description-${id}`;
    const inputId = `file-input-${id}`;
    let ariaDescribedByIDList = [
        descriptionId,
        ariaDescribedBy
    ].filter(Boolean);
    if (isUploadError(uploadStatus) && uploadStatus.errorMessageId) {
        ariaDescribedByIDList = [
            uploadStatus.errorMessageId,
            ...ariaDescribedByIDList
        ];
    }
    // This should be text like "File upload" to describe what kind of component this is.
    const uploadScreenReaderRole = hasLabel('upload.screenreader.title') ? getLabel('upload.screenreader.title') : undefined;
    const uploadedFileName = isUploadSuccess(uploadStatus) ? uploadStatus.uploadReference.fileName : undefined;
    const uploadScreenReaderName = !!uploadedFileName ? `${title} ${uploadedFileName}` : title;
    const onFileDropSuccess = React__default.useCallback((files)=>{
        const file = files[0] // because uploading multiple files is disabled, we only need to take the first one.
        ;
        onFileAccepted(file);
    }, [
        onFileAccepted
    ]);
    const onFileDropRejected = React__default.useCallback((fileRejections)=>{
        const fileRejection = fileRejections[0];
        onFileRejected(fileRejection);
    }, [
        onFileRejected
    ]);
    const onDeleteClick = React__default.useCallback((event)=>{
        var ref;
        event.stopPropagation();
        onDelete();
        (ref = rootRef.current) === null || ref === void 0 ? void 0 : ref.focus();
    }, [
        onDelete
    ]);
    const onCancelClick = React__default.useCallback((event)=>{
        var ref;
        event.stopPropagation();
        onCancel();
        (ref = rootRef.current) === null || ref === void 0 ? void 0 : ref.focus();
    }, [
        onCancel
    ]);
    const isDisabled = disabled || isUploadLoading(uploadStatus);
    const { getRootProps , getInputProps , isDragActive , isDragAccept , isDragReject , rootRef ,  } = useDropzone({
        accept: allowedFileTypes,
        maxFiles: 1,
        multiple: false,
        maxSize: maximumFileSizeBytes,
        onDropAccepted: onFileDropSuccess,
        onDropRejected: onFileDropRejected,
        disabled: isDisabled
    });
    const iconSize = variant === 'vertical' ? 84 : 48;
    return(/*#__PURE__*/ React__default.createElement("div", _extends$1({}, getRootProps(), {
        tabIndex: isDisabled ? -1 : 0,
        className: classnames('upload', `upload--${variant}`, className, {
            'upload--file-hover': isDragActive,
            'upload--active': isDragActive || isUploadSuccess(uploadStatus),
            'upload--accept': isDragAccept || isUploadSuccess(uploadStatus),
            'upload--reject': isDragReject,
            'upload--error': isUploadError(uploadStatus),
            'upload--loading': isUploadLoading(uploadStatus)
        }),
        id: id,
        role: "button",
        "aria-label": uploadScreenReaderName,
        "aria-describedby": ariaDescribedByIDList.join(' '),
        "aria-roledescription": uploadScreenReaderRole
    }), /*#__PURE__*/ React__default.createElement("div", {
        className: "upload__inner"
    }, /*#__PURE__*/ React__default.createElement(AnimatePresence, null, isUploadSuccess(uploadStatus) && /*#__PURE__*/ React__default.createElement(motion.div, _extends$1({
        className: "upload__icon upload__icon--valid"
    }, iconSharedProps), /*#__PURE__*/ React__default.createElement(Icon, {
        name: "checkmark"
    })), isUploadError(uploadStatus) && /*#__PURE__*/ React__default.createElement(motion.div, _extends$1({
        className: "upload__icon upload__icon--invalid"
    }, iconSharedProps), /*#__PURE__*/ React__default.createElement(Icon, {
        name: "alert-circle"
    })), isUploadLoading(uploadStatus) && /*#__PURE__*/ React__default.createElement(motion.div, _extends$1({
        className: "upload__icon upload__icon--loading"
    }, iconSharedProps), /*#__PURE__*/ React__default.createElement("div", {
        className: "sr-only",
        role: "alert"
    }, getLabel('common.loadingText')), /*#__PURE__*/ React__default.createElement(Loader, {
        className: "upload__loader"
    }))), /*#__PURE__*/ React__default.createElement(Icon, {
        width: iconSize,
        height: iconSize,
        name: iconName
    }), /*#__PURE__*/ React__default.createElement("label", {
        className: "upload__label",
        htmlFor: inputId
    }, isUploadSuccess(uploadStatus) ? /*#__PURE__*/ React__default.createElement(Text, {
        variant: "body",
        className: "upload__file-name"
    }, uploadStatus.uploadReference.fileName) : /*#__PURE__*/ React__default.createElement("span", {
        className: "upload__title"
    }, title)), /*#__PURE__*/ React__default.createElement("input", _extends$1({}, getInputProps({
        disabled
    }), {
        "aria-describedby": ariaDescribedByIDList.join(' '),
        "data-t": "upload-input",
        id: inputId
    })), isUploadSuccess(uploadStatus) && /*#__PURE__*/ React__default.createElement(IconButton, {
        onClick: onDeleteClick,
        "aria-label": `${getLabel('common.actions.remove')} ${uploadedFileName}`,
        className: "upload__delete"
    }, /*#__PURE__*/ React__default.createElement(Icon, {
        name: "trash"
    })), isUploadLoading(uploadStatus) && /*#__PURE__*/ React__default.createElement(IconButton, {
        onClick: onCancelClick,
        "aria-label": getLabel('common.actions.cancel'),
        className: "upload__delete"
    }, /*#__PURE__*/ React__default.createElement(Icon, {
        name: "trash"
    }))), /*#__PURE__*/ React__default.createElement("div", {
        id: descriptionId,
        className: "upload__description"
    }, /*#__PURE__*/ React__default.createElement(Text, {
        variant: "body-head"
    }, getFormattedLabel('upload.description', {
        parseHTML: true
    })))));
};

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _asyncToGenerator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
function _objectWithoutProperties(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
var UploadContainer = (_param)=>{
    var { scope , onStatusChange , uploadStatus , referenceId  } = _param, rest = _objectWithoutProperties(_param, [
        "scope",
        "onStatusChange",
        "uploadStatus",
        "referenceId"
    ]);
    const { getToken  } = useRecaptcha();
    const { apiUrl  } = useApiConfig();
    const { configs  } = useUpload();
    const scopeConfig = configs[scope];
    const controllerRef = React__default.useRef();
    const handleDropSuccess = React__default.useCallback(function() {
        var _ref = _asyncToGenerator(function*(file) {
            if (!scopeConfig || !statusSuccess(scopeConfig)) {
                return;
            }
            onStatusChange === null || onStatusChange === void 0 ? void 0 : onStatusChange(uploadStatusLoading());
            const { result: { isRecaptchaTokenRequired , maxFileNameLength  } ,  } = scopeConfig;
            try {
                var ref, ref1;
                controllerRef.current = new AbortController();
                const recaptchaToken = isRecaptchaTokenRequired ? yield getToken() : undefined;
                const { token: uploadToken  } = yield Upload$1.getUploadToken(apiUrl, {
                    scope,
                    referenceId,
                    recaptchaToken
                }, {
                    signal: (ref = controllerRef.current) === null || ref === void 0 ? void 0 : ref.signal
                });
                const isFileNameTooLong = file.name.length > maxFileNameLength;
                const { storageId , storageToken  } = yield Upload$1.sendFile(apiUrl, _objectSpread({
                    scope,
                    referenceId,
                    uploadedFile: file,
                    uploadToken
                }, isFileNameTooLong ? {
                    alias: file.name.slice(0, maxFileNameLength)
                } : {}), {
                    signal: (ref1 = controllerRef.current) === null || ref1 === void 0 ? void 0 : ref1.signal
                });
                const fileRef = {
                    storageId,
                    storageToken,
                    fileName: file.name
                };
                // Drop success and upload success
                onStatusChange === null || onStatusChange === void 0 ? void 0 : onStatusChange(uploadStatusSuccess(fileRef));
            } catch (error) {
                // Drop success but upload failure
                error.name === 'AbortError' ? onStatusChange === null || onStatusChange === void 0 ? void 0 : onStatusChange(uploadStatusIdle()) : onStatusChange === null || onStatusChange === void 0 ? void 0 : onStatusChange(uploadStatusError(error));
            } finally{
                controllerRef.current = undefined;
            }
        });
        return function(file) {
            return _ref.apply(this, arguments);
        };
    }(), [
        scopeConfig,
        onStatusChange,
        referenceId
    ]);
    const handleDropError = React__default.useCallback((fileRejection)=>{
        onStatusChange === null || onStatusChange === void 0 ? void 0 : onStatusChange(uploadStatusError(fileRejection));
    }, [
        onStatusChange
    ]);
    const handleCancel = React__default.useCallback(()=>{
        var ref;
        (ref = controllerRef.current) === null || ref === void 0 ? void 0 : ref.abort();
    }, []);
    const handleDelete = React__default.useCallback(()=>{
        onStatusChange === null || onStatusChange === void 0 ? void 0 : onStatusChange(uploadStatusIdle('FILE_REMOVED'));
    }, [
        onStatusChange
    ]);
    if (!scopeConfig || statusFailed(scopeConfig) || statusWarning(scopeConfig)) {
        // TODO: Do we need to account for a warning status?
        // TODO: Error view to be implemented in MACC-5453
        return(/*#__PURE__*/ React__default.createElement("p", {
            color: "red"
        }, "Could not fetch config for this upload."));
    }
    if (statusLoading(scopeConfig) || statusUninit(scopeConfig)) {
        // TODO: Loading view to be implemented in MACC-5453
        return(/*#__PURE__*/ React__default.createElement("p", null, "Fetching config..."));
    }
    const { maxFileSizeBytes , allowedFileTypes  } = scopeConfig.result;
    return(/*#__PURE__*/ React__default.createElement(Upload, _extends({}, rest, {
        uploadStatus: uploadStatus,
        onFileAccepted: handleDropSuccess,
        onFileRejected: handleDropError,
        onDelete: handleDelete,
        onCancel: handleCancel,
        maximumFileSizeBytes: maxFileSizeBytes,
        allowedFileTypes: allowedFileTypes || []
    })));
};

export { types as Types, UploadContainer as Upload, UploadProvider, useUpload, useUploadHandler };
//# sourceMappingURL=index.js.map
